import type { JSX } from 'react';
import { styled } from 'styled-components';
import { Badge } from '../Badge';
import { useColorPickerState } from './ColorPickerContext';
import { Icon } from '../Icon';
import { Text } from '../Text';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
`;

// We don't want to just do toFixed(1) because it would round a number like 4.45
// to 4.5, which would make our UI misleading, as 4.45 doesn't meet the minimum
// contrast ratio of 4.5:1. We want 4.45 to display as 4.4:1, so we floor it to
// the nearest tenth.
const floorToTenth = (value: number): number => {
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  return Math.floor(value * 10) / 10;
};

export const ContrastIndicator = (): JSX.Element => {
  const { currentContrastRatio, minimumContrastRatio } = useColorPickerState();
  const isContrastSufficient = currentContrastRatio >= minimumContrastRatio;

  const label = (
    <div>
      <Text
        renderAs="span"
        variant="body4"
      >
        Contrast:
      </Text>{' '}
      <Text
        renderAs="span"
        variant="body4Mono"
      >
        {floorToTenth(currentContrastRatio).toFixed(1)}:1
      </Text>
    </div>
  );

  return (
    <Container>
      {isContrastSufficient ? (
        <Badge
          colorScheme="success"
          icon={<Icon type="checkmark" />}
          label={label}
          variant="outline"
        />
      ) : (
        <Badge
          colorScheme="warning"
          icon={<Icon type="error" />}
          label={label}
          variant="outline"
        />
      )}
    </Container>
  );
};
